import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useUserQuery } from '@/api/queries/useAuth';
import { useCommentableTextAtoms } from '@/comments';
import { CommentBlock } from '@/components/CommentBlock/CommentBlock';
import { Loading } from '@/components/Loading/Loading';
import { Notification } from '@/components/Notification/Notification';
import { styles, TextDivider } from '@/components/StyledComponents';
import { GlassPanel } from '@/components/ui/glass-panel';
import { TEXT_INK, Text } from '@/components/ui/text';
import { useCommentDetail } from '@/data/entities/comments';
import { useNotificationsList } from '@/data/entities/notifications';
import type { CommentableModel } from '@/enums';
import { useProjectId, useToken } from '@/hooks';
import { useCommentWebSocket } from '@/hooks/useCommentWebSocket';
import { useNotificationWebSocket } from '@/hooks/useNotificationWebSocket';

export const NotificationsButton = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clear } = useCommentableTextAtoms();
    const [open, setOpen] = useState(false);
    const projectId = useProjectId();
    const token = useToken();
    const { data: userData } = useUserQuery();
    const id = userData?.id;
    const [commentable, setCommentable] = useState<{
        id: number;
        project_id: number;
        type: CommentableModel;
        comment_id: number;
        title: string;
    } | null>(null);

    const { data: notifications } = useNotificationsList({}, { enabled: !!projectId && !!id });
    useNotificationWebSocket(id || null, token);

    const { data: comment, isFetching } = useCommentDetail(
        {
            id: commentable?.comment_id ?? 0,
            projectId: commentable?.project_id ?? 0,
            commentableModel: commentable?.type ?? ('scene' as never),
            commentableId: commentable ? [commentable.id] : [],
        },
        { enabled: !!commentable },
    );
    useCommentWebSocket(commentable?.comment_id || null, token);

    const handleSelectComment = (arg: { project_id: number; id: number; type: CommentableModel; comment_id: number; title: string }) =>
        setCommentable(arg);

    const handleCloseDrawer = () => {
        setOpen(false);
        setCommentable(null);
        clear();
    };

    return (
        <div>
            {notifications ? (
                <>
                    <NotificationsIcon
                        onClick={() => setOpen(true)}
                        fontSize="small"
                        color={notifications.some(item => item.read_at === null) ? 'secondary' : 'info'}
                        sx={{
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                        }}
                    />

                    <GlassPanel open={open} onClose={handleCloseDrawer} title={t('notifications')} className="w-[480px]">
                        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2">
                            {commentable ? (
                                <>
                                    <div className="mb-[20px]">
                                        <KeyboardBackspaceIcon
                                            sx={{ cursor: 'pointer' }}
                                            color="info"
                                            onClick={() => setCommentable(null)}
                                        />
                                        <div className="text-center">
                                            <Text
                                                variant="subtitle1"
                                                color={TEXT_INK}
                                                children={commentable.title}
                                                className="mb-[30px] inline-block"
                                            />
                                            <Box
                                                onClick={() => {
                                                    if (comment?.length) {
                                                        const url = new URL(comment[0]?.action_url ?? '');
                                                        navigate(url);
                                                        handleCloseDrawer();
                                                    }
                                                }}
                                                sx={[
                                                    styles.iconAccent,
                                                    {
                                                        width: '30px',
                                                        height: '30px',
                                                        borderRadius: '10em',
                                                        margin: '-3px 0 0 16px',
                                                    },
                                                ]}
                                            >
                                                <ArrowForwardIcon fontSize="small" color="secondary" />
                                            </Box>
                                        </div>
                                    </div>

                                    {comment && !isFetching ? (
                                        comment.map(item => {
                                            return (
                                                <CommentBlock
                                                    key={item.id}
                                                    {...item}
                                                    open
                                                    scrollToId={commentable.comment_id}
                                                    project_id={commentable.project_id}
                                                />
                                            );
                                        })
                                    ) : (
                                        <Loading />
                                    )}
                                </>
                            ) : notifications.length ? (
                                <>
                                    {notifications
                                        .filter(item => item.read_at === null)
                                        .map(item => (
                                            <Notification
                                                {...item}
                                                key={item.id}
                                                handleCloseDrawer={handleCloseDrawer}
                                                handleSelectComment={handleSelectComment}
                                            />
                                        ))}

                                    {notifications.some(item => Boolean(item.read_at)) ? (
                                        <>
                                            <TextDivider>
                                                <Text
                                                    variant="body1"
                                                    color="text-info-main"
                                                    children={t('notifications-old')}
                                                    className="text-center"
                                                />
                                            </TextDivider>
                                            {notifications
                                                .filter(item => item.read_at !== null)
                                                .map(item => {
                                                    return (
                                                        <Notification
                                                            {...item}
                                                            key={item.id}
                                                            handleCloseDrawer={handleCloseDrawer}
                                                            handleSelectComment={handleSelectComment}
                                                        />
                                                    );
                                                })}
                                        </>
                                    ) : null}
                                </>
                            ) : (
                                <Text
                                    variant="body1"
                                    color="text-info-main"
                                    children={t('notifications-empty')}
                                    className="mb-[30px] text-center"
                                />
                            )}
                        </div>
                    </GlassPanel>
                </>
            ) : null}
        </div>
    );
};
